import Konva from "konva";
import React, { useMemo } from "react";

import { Impulse, type ReadonlyImpulse, useScoped } from "~/tools/impulse";
import type { Size } from "~/tools/size";
import { useRequiredContext } from "~/tools/use-required-context";

export interface ContainerContextValue {
	instance: Konva.Container;
	size: ReadonlyImpulse<Size>;
}

const ContainerContext = React.createContext<null | ContainerContextValue>(null);

export const ContainerContextProvider = ContainerContext.Provider;

export const useInContainer = (node: Konva.Node): void => {
	const context = React.useContext(ContainerContext);

	React.useLayoutEffect(() => {
		const isStage = node instanceof Konva.Stage;
		// Stage is an root container, so it is expected
		if (isStage && context == null) {
			return;
		}

		if (context == null) {
			throw new Error("Expecting <Container> in the component tree");
		}

		if (isStage) {
			throw new Error("Stage can't have another Stage");
		}

		context.instance.add(node);

		return () => {
			node.remove();
		};
	}, [context, node]);
};

export const useSize = (ownSize: Partial<Size>): ReadonlyImpulse<Size> => {
	const parent = React.useContext(ContainerContext);

	return useMemo(
		() =>
			Impulse((scope) => ({
				width: ownSize.width ?? parent?.size.getValue(scope).width ?? 0,
				height: ownSize.height ?? parent?.size.getValue(scope).height ?? 0,
			})),
		[ownSize.width, ownSize.height, parent?.size],
	);
};

export const useContainerSize = (): Size => {
	const { size } = useRequiredContext(ContainerContext, {
		providerName: "Container",
	});

	return useScoped(size);
};
